import { useTranslations } from "next-intl";
import { useMemo } from "react";

import { urls } from "@shared/constants";
import { appendUtmParams } from "@shared/tracking";

import type useSubmissionStatus from "./useSubmissionStatus";
import useUtmParams from "./useUtmParams";

type UseSubmissionCTAButtonInput = {
  status: ReturnType<typeof useSubmissionStatus>;
  callId: string;
  callSlug: string;
  useNewSubmissionWizard: boolean;
  isCallManager?: boolean;
  /**
   * Whether the call is still a draft (not published yet). Only call managers
   * may start or continue a submission to a draft call, to test it before it
   * goes live.
   */
  isDraftCall?: boolean;
  /**
   * Where the CTA is rendered (e.g. "call_grid", "call_page"). Appended as
   * utm_medium unless an inbound campaign already set one.
   */
  utmMedium?: string;
};
export default function useSubmissionCTAButton({
  status,
  callId,
  /**
   * Be careful not to use the call-page slug here, as it might
   * be different from the call slug (e.g. for multi-lingual calls)
   */
  callSlug,
  /**
   * Whether to use the simplified submission wizard or not. This
   * might be a temporary flag until the new submission wizard is
   * replacing the old one (which might take some time).
   */
  useNewSubmissionWizard,
  isCallManager,
  isDraftCall,
  utmMedium,
}: UseSubmissionCTAButtonInput) {
  const t = useTranslations("packages.hooks.useSubmissionCTAButton");
  const utmParams = useUtmParams({ utm_medium: utmMedium });

  const mainButton = useMemo(() => {
    // Viewing a finished submission stays possible, e.g. when a live call is
    // set back to draft
    if (isDraftCall && !isCallManager && status !== "SUBMITTED")
      return undefined;
    if (status === "UPCOMING" && isCallManager)
      return {
        href: appendUtmParams(
          useNewSubmissionWizard
            ? urls.startSimplifiedSubmission(callSlug)
            : urls.startSubmission(callId, true),
          utmParams,
        ),
        label: t("start"),
      };
    if (status === "OPEN")
      return {
        href: appendUtmParams(
          useNewSubmissionWizard
            ? urls.startSimplifiedSubmission(callSlug)
            : urls.startSubmission(callId, true),
          utmParams,
        ),
        label: t("start"),
      };
    if (status === "DRAFT")
      return {
        href: appendUtmParams(
          useNewSubmissionWizard
            ? urls.startSimplifiedSubmission(callSlug)
            : urls.startSubmission(callId),
          utmParams,
        ),
        label: t("continue"),
      };
    if (status === "IN_EDIT")
      return {
        href: appendUtmParams(urls.finishSubmission(callId), utmParams),
        label: t("continue"),
      };
    if (status === "SUBMITTED")
      return {
        href: appendUtmParams(urls.viewSubmission(callId), utmParams),
        label: t("view"),
      };
    return undefined;
  }, [
    status,
    callId,
    callSlug,
    useNewSubmissionWizard,
    isCallManager,
    isDraftCall,
    utmParams,
    t,
  ]);
  return mainButton;
}

useSubmissionCTAButton.messages = ["packages.hooks.useSubmissionCTAButton"];
